import React, { useState, useEffect, useCallback } from "react";
import { StyledEngineProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CreateUserRole from "./CreateUserRole";
import UserList from "./UserList";
import {
  createUser,
  fetchAllUsers,
  getAllPartners,
  getUsersRolesSelect,
  updateUser,
} from "../../../services/tenant/tenant";
import { toast } from "../../../components/AppToast";
import { useDispatch, useSelector } from "react-redux";
import { formatToDisplayDate } from "../../../utils/dateFormatter";
import { getallfacility, getallfacilitySelect } from "../../../services/facility/facility";

const UserRoleManagement = () => {
  const dispatch = useDispatch();
  const { _id } = useSelector((state) => state?.login?.userDetail);

  // Data
  const [partners, setPartners] = useState([]);
  const [roleData, setRoleData] = useState([]);

  // UI modes
  const [isCreating, setIsCreating] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [facilityData, setFacilityData] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);

  // ---------- Fetch partners & roles ----------
  useEffect(() => {
    console.log("id",_id);
    
    const fetchPartners = async () => {
      try {
        const res = await getAllPartners(_id);
        setPartners(
          res?.map((p) => ({ label: p.partner_name, value: p._id })) || []
        );
      } catch (err) {
        toast.error("Failed to fetch partners");
      }
    };

    const fetchRoles = async () => {
      try {
        const res = await getUsersRolesSelect();
        setRoleData(
          res?.map((item) => ({
            label: item.roleID,
            value: item._id,
            roleType: item.roleType,
            partnerId: item.partnerId || {},
          })) || []
        );
      } catch (err) {
        toast.error("Failed to fetch roles");
      }
    };

    const fetchFacility = async () => {
      try {
        const data = await getallfacilitySelect();
        const option =
          data?.map((item) => ({
            label: item?.Facility_ID,
            value: item?._id,
            facilityType: item?.Facility_Owner_Type,
            partnerId: item?.partnerId || {},
          })) || [];
        setFacilityData(option);
      } catch (err) {
        console.error("Failed to fetch facilities:", err);
      }
    };

    fetchPartners();
    fetchRoles();
    fetchFacility();
  }, [_id]);

  // ---------- Handlers ----------
  const handleCreateClick = () => {
    setIsCreating(true);
    setActiveUser(null);
  };

  const handleViewUser = (row) => {
    if (!row) return toast.error("Invalid user data");
    setActiveUser({ mode: "view", initialValues: row });
    setIsCreating(false);
  };

  const handleEditUser = (row) => {

    if (!row) return toast.error("Invalid user data");
    setActiveUser({ mode: "edit", initialValues: row });

    setEditingUserId(row.id);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setActiveUser(null);
    setIsCreating(false);
    setEditingUserId(null);
    setListVersion((v) => v + 1); // refresh table
  };

  const handleSubmit = async (formData) => {
    try {
      await createUser(formData);
      toast.success("User created successfully");
      handleCancel();
    } catch (err) {
      toast.error("Failed to create user");
    }
  };

  const handleUpdateSubmit = async (formData) => {
    if (!editingUserId) return toast.error("No user selected for update");
    try {
      await updateUser(formData, editingUserId);
      toast.success("User updated successfully");
      handleCancel();
    } catch (err) {
      toast.error("Failed to update user");
    }
  };

  const onlyFormVisible = isCreating || !!activeUser;

  return (
    <div className="card shadow-lg main-card" style={{ position: "relative" }}>
      <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
        <h4 className="mb-0 h6 fw-semibold">
          {activeUser
            ? activeUser.mode === "view"
              ? "View User"
              : "Edit User"
            : isCreating
              ? "Add New User"
              : "User Management"}
        </h4>
      </div>

      {/* Breadcrumbs */}
      {(isCreating || activeUser) && (
        <nav aria-label="breadcrumb" className="px-3 py-2 border-bottom bg-light mb-2">
          <ol className="breadcrumb mb-0 align-items-center">
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-muted"
                onClick={handleCancel}
              >
                User Management
              </button>
            </li>
            <li className="breadcrumb-item active fw-semibold text-primary" aria-current="page">
              {isCreating
                ? "Create User"
                : activeUser?.mode === "edit"
                  ? "Edit User"
                  : "View User"}
            </li>
          </ol>
        </nav>
      )}

      <div className="card-body">
        <StyledEngineProvider injectFirst>
          {/* User List */}
          {!onlyFormVisible && (
            <UserList
              onView={handleViewUser}
              onEdit={handleEditUser}
              refreshKey={listVersion}
              showCreateButton={true}
              createButtonText="Add New User"
              createButtonIcon="bi-plus-lg"
              onCreateClick={handleCreateClick}
            />
          )}

          {/* Create User */}
          {isCreating && !activeUser && (
            <CreateUserRole
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              submitText="Create User"
              cancelText="Back"
              partners={partners}
              roleData={roleData}
              facilityData={facilityData}
              readOnly={false}
            />
          )}

          {/* Edit / View User */}
          {activeUser && (
            <CreateUserRole
              initialValues={activeUser.initialValues}
              onSubmit={handleUpdateSubmit}
              onCancel={handleCancel}
              submitText={activeUser.mode === "edit" ? "Update User" : "Close"}
              cancelText="Back"
              partners={partners}
              roleData={roleData}
              facilityData={facilityData}
              readOnly={activeUser.mode === "view"}
              isEdit={activeUser?.mode === "edit"}
            />
          )}
        </StyledEngineProvider>
      </div>
    </div>
  );
};

export default UserRoleManagement;
